/**
 * CDS-1 — Recursive `undefined` scrubber for Firestore writes.
 *
 * Why this exists
 * ----------------
 * The Firebase web SDK (when initialised WITHOUT
 * `ignoreUndefinedProperties`) rejects any payload that carries a key
 * whose value is `undefined` — `setDoc(ref, { foo: undefined })` throws
 *
 *   "Function setDoc() called with invalid data. Unsupported field
 *    value: undefined (found in field foo)"
 *
 * The project's `src/firebase.ts` deliberately leaves the flag off so
 * we catch optional-typed fields that have leaked through type
 * narrowing. The convention is to scrub at the write site.
 *
 * Previously this lived as a private function in `src/store/index.ts`
 * (lines 807-819). It now lives here as a shared helper so non-store
 * call sites — the extraction cache (`extractionCache.firestoreWrite`),
 * future evidence/claim writers — can use the same scrub without
 * importing the entire store (and the circular-import vibe that
 * implies). `store/index.ts` re-exports the symbol so existing call
 * sites stay unchanged.
 *
 * Contract
 * --------
 *   - `null` and primitive values: passed through untouched.
 *   - Plain objects (`Object.prototype` proto chain): walked
 *     recursively; any key whose value is `undefined` is dropped.
 *   - Arrays: walked recursively.
 *   - Non-plain objects (Date, Map, Set, class instances): passed
 *     through as-is. Firestore handles Date natively; the caller
 *     shouldn't be writing Map/Set/class instances anyway.
 *
 * Engineering invariants
 * ----------------------
 *   - Pure function. No mutation of the input.
 *   - Idempotent — `strip(strip(x))` deep-equals `strip(x)`.
 *   - Type-preserving signature `<T>(value: T): T`. The return type
 *     intentionally lies (the structural shape may have fewer keys);
 *     this matches the previous in-store helper so the migration is
 *     drop-in.
 */
export function stripUndefined<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(stripUndefined) as unknown as T;
  // Skip non-plain objects (Date, etc.) — Firestore handles them natively.
  const proto = Object.getPrototypeOf(value);
  if (proto !== null && proto !== Object.prototype) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[k] = stripUndefined(v);
  }
  return out as T;
}

/**
 * Heuristic: is this value a Firestore FieldValue sentinel (deleteField(),
 * serverTimestamp(), arrayUnion(), increment(), …)?
 *
 * These are opaque class instances the SDK only understands inside a write
 * payload. They must NEVER reach component render state — a `deleteField()`
 * sentinel placed in `site.customerLogoUrl` would leak into an `<img src>`.
 * We detect them structurally (non-plain object exposing the SDK's
 * `_methodName` marker) so we don't have to import the SDK here.
 */
export function isFieldValueSentinel(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  if (proto === null || proto === Object.prototype) return false; // plain object / null-proto
  // Firebase web SDK FieldValue instances carry an internal `_methodName`
  // (e.g. 'deleteField', 'serverTimestamp'). Cheap, version-stable signal.
  return '_methodName' in (value as Record<string, unknown>);
}

/**
 * Task #65 — reconcile a freshly-read Firestore doc with the caller's own
 * write so a stale cached read can't resurrect a field the caller just
 * cleared.
 *
 * `updateSite` reads the canonical doc back after writing to refresh stale
 * SIBLING fields. But `getDoc` can resolve from the local cache before the
 * just-issued mutation lands there, so the read may still carry the OLD value
 * of a field the caller just set (the "customer logo returns after Remove"
 * bug). The caller's `updates` are authoritative for the keys they explicitly
 * touched — re-apply them on top of the fresh doc.
 *
 * FieldValue sentinels (e.g. `deleteField()`) in `updates` are normalised to
 * `null` in the merged result so the sentinel can't leak into render state;
 * the actual field removal still happens server-side via the write payload.
 */
export function applyAuthoritativeUpdates<T extends object>(
  fresh: T,
  updates: Partial<T>,
): T {
  const out: Record<string, unknown> = { ...(fresh as Record<string, unknown>) };
  for (const [k, v] of Object.entries(updates as Record<string, unknown>)) {
    if (v === undefined) continue; // not part of the write (stripped) — keep fresh value
    out[k] = isFieldValueSentinel(v) ? null : v;
  }
  return out as T;
}
